import { AlertTriangle } from "lucide-react";

export const ROLE_OPTIONS = [
  { value: "author_only", label: "Authors only" },
  { value: "reader_only", label: "Beta Readers only" },
  { value: "both", label: "Both" },
];

export const ACTIVITY_OPTIONS = [
  { value: "any", label: "Any project activity" },
  { value: "published", label: "Published a real project" },
  { value: "never", label: "Never published a real project" },
  { value: "active", label: "Has active projects" },
];

const hasAuthorGroup = (roles) => roles.includes("author_only") || roles.includes("both");

export const audienceText = (roles, activity) => {
  if (!roles.length) return "All Members";
  const r = ROLE_OPTIONS.filter((o) => roles.includes(o.value)).map((o) => o.label).join(" + ");
  if (activity === "any" || !hasAuthorGroup(roles)) return r;
  return `${r} · ${ACTIVITY_OPTIONS.find((o) => o.value === activity)?.label}`;
};

const roleGroup = (r) => (r.is_author && r.is_reader ? "both" : r.is_author ? "author_only" : r.is_reader ? "reader_only" : null);

// Client-side mirror of the server role/activity rules, used to flag selections outside the filters.
export const matchesFilters = (r, roles, activity) => {
  if (roles.length && !roles.includes(roleGroup(r))) return false;
  if (activity === "published") return r.has_published;
  if (activity === "never") return r.is_author && !r.has_published;
  if (activity === "active") return r.has_active;
  return true;
};

const chipClass = (on) => `rounded-full border px-3 py-2 text-xs font-medium transition-colors min-h-[44px] ${
  on
    ? "bg-[hsl(var(--sb-forest))] text-white border-[hsl(var(--sb-forest))]"
    : "bg-white text-neutral-900 hover:bg-neutral-100 border-neutral-300"
}`;

export function EmailRecipientFilters({ roles, activity, onRoles, onActivity, includeQa, onIncludeQa, qaCount, demoCount }) {
  const showActivity = hasAuthorGroup(roles);
  const toggle = (v) => {
    const next = roles.includes(v) ? roles.filter((x) => x !== v) : [...roles, v];
    onRoles(next);
  };
  return (
    <div className="space-y-3" data-testid="admin-email-filters">
      <div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">Role <span className="normal-case tracking-normal">(tap several to combine)</span></div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Role filter">
          <button
            type="button"
            aria-pressed={roles.length === 0}
            onClick={() => onRoles([])}
            data-testid="admin-email-role-all"
            className={chipClass(roles.length === 0)}
          >
            All Members
          </button>
          {ROLE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              aria-pressed={roles.includes(o.value)}
              onClick={() => toggle(o.value)}
              data-testid={`admin-email-role-${o.value}`}
              className={chipClass(roles.includes(o.value))}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      {showActivity && (
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Author project activity</span>
          <select
            className="sb-input mt-1.5 text-sm"
            value={activity}
            onChange={(e) => onActivity(e.target.value)}
            data-testid="admin-email-activity-filter"
          >
            {ACTIVITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      )}
      <div className="rounded-xl border border-border/70 p-3 text-xs space-y-1" data-testid="admin-email-test-accounts">
        <label className="flex items-center gap-2 cursor-pointer font-medium min-h-[44px]">
          <input
            type="checkbox"
            className="h-5 w-5 accent-[hsl(var(--sb-forest))]"
            checked={includeQa}
            onChange={(e) => onIncludeQa(e.target.checked)}
            data-testid="admin-email-include-qa"
          />
          <span>Include internal QA accounts ({qaCount} opted in)</span>
        </label>
        <p className="text-muted-foreground" data-testid="admin-email-demo-excluded">
          Demo accounts are always excluded ({demoCount} opted-in demo account{demoCount === 1 ? "" : "s"} skipped).
        </p>
      </div>
    </div>
  );
}

export function RecipientBadges({ r }) {
  const role = r.is_author && r.is_reader ? "Both" : r.is_author ? "Author" : r.is_reader ? "Beta Reader" : null;
  const chip = "rounded-full border px-1.5 py-0.5 text-[10px] font-medium";
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {role && <span className={`${chip} bg-white text-neutral-900 border-neutral-300`}>{role}</span>}
      {r.has_active && <span className={`${chip} bg-[hsl(var(--sb-forest))] text-white border-[hsl(var(--sb-forest))]`}>Active project</span>}
      {r.has_published && !r.has_active && <span className={`${chip} bg-amber-200 text-amber-950 border-amber-500`}>Published</span>}
      {r.is_qa && <span className={`${chip} bg-sky-200 text-sky-950 border-sky-500`} data-testid={`admin-email-qa-badge-${r.id}`}>QA account</span>}
    </div>
  );
}

export function OutsideFilterWarning({ count, label, onRemove, onReview, testid }) {
  return (
    <div role="alert" className="rounded-xl border border-amber-500 bg-amber-100 text-amber-950 p-3 text-xs" data-testid={testid}>
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
        <p>
          <span className="font-semibold">{count} selected recipient{count === 1 ? " doesn't" : "s don't"} match {label}.</span>{" "}
          They&rsquo;re still selected and will receive this email unless you remove them.
        </p>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={onRemove} className="rounded-full bg-amber-950 text-amber-50 px-3 py-1.5 font-medium min-h-[44px]" data-testid={`${testid}-remove`}>
          Remove {count} from selection
        </button>
        {onReview && (
          <button type="button" onClick={onReview} className="rounded-full border border-amber-700 px-3 py-1.5 font-medium min-h-[44px]" data-testid={`${testid}-review`}>
            Review selection
          </button>
        )}
      </div>
    </div>
  );
}

export function SendConfirmDialog({ audience, count, subject, outsideCount, outsideLabel, onRemoveOutside, sending, onCancel, onConfirm }) {
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-3 sm:p-4" data-testid="admin-email-confirm-modal">
      <div className="absolute inset-0 bg-foreground/60" onClick={onCancel} />
      <div className="relative max-w-md w-full max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-background shadow-xl p-5 sm:p-6">
        <h3 className="font-serif text-2xl">Confirm send</h3>
        <dl className="mt-4 space-y-3 text-sm">
          <div>
            <dt className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Audience</dt>
            <dd className="font-medium break-words" data-testid="admin-email-confirm-audience">{audience}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Recipients</dt>
            <dd className="font-medium" data-testid="admin-email-confirm-count">{count} opted-in recipient{count === 1 ? "" : "s"}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Subject</dt>
            <dd className="font-medium break-words" data-testid="admin-email-confirm-subject">{subject}</dd>
          </div>
        </dl>
        {outsideCount > 0 && (
          <div className="mt-4">
            <OutsideFilterWarning count={outsideCount} label={outsideLabel} onRemove={onRemoveOutside} testid="admin-email-confirm-outside-warning" />
          </div>
        )}
        <p className="mt-4 text-xs text-muted-foreground">Opted-out, unsubscribed and demo accounts are always excluded. Each person receives one email, even if they hold several roles.</p>
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button className="btn-ghost border min-h-[44px]" onClick={onCancel} data-testid="admin-email-confirm-cancel">Cancel</button>
          <button className="btn-primary min-h-[44px] disabled:opacity-50" onClick={onConfirm} disabled={sending || count === 0} data-testid="admin-email-confirm-send">
            {sending ? "Sending…" : `Send to ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}
